import React, { useCallback, useEffect, useRef, useState } from 'react'
import OrientationDirector, { Orientation } from 'react-native-orientation-director'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import { ScrollView, View } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useHasLargeScreen, useShouldLockToPortrait } from '@src/config/device'
import { useDispatchAsync } from '@utils'
import { CommonRefreshControl } from '@components'
import { Button, Icon, Text } from '@components/base'
import { AccountAvatarNavigationBar } from '@src/components/navigationBar/AccountAvatarNavigationBar'
import { LiveSurfaceBanner } from '@src/components/notifications/banners/LiveSurfaceBanner'
import { useRefreshNotificationsOnFocus } from '@src/hooks/useRefreshNotificationsOnFocus'
import { NotificationEngineScreen } from '@src/types'
import StreakIcon from '@src/assets/svgs/streak'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { DateSelectorMode } from '@src/components/DateSelector/types'
import { ContentLoader, Rect } from '@src/components/ContentLoader'
import { ActionGoalProgress } from '../ActionGoals/components/ActionGoalProgress'
import { OnboardingChecklistBanner } from '../Home/components/Banners/OnboardingChecklistBanner'
import { VideoCallsUpsellBanner } from '../Home/components/Banners/VideoCallsUpsellBanner'
import { startOfCurrentWeek } from '../ActionGoals/utils'
import { ActionGoalSummaryLoadingCard } from '../ActionGoals/components/ActionGoalSummaryLoadingCard'
import {
  isSummaryForWeekCachedSelector,
  summariesForWeekSelector,
} from '../ActionGoals/models/actionGoals.selectors'
import { MIN_ACTION_GOAL_CARDS, SUMMARY_CARD_HEIGHT } from '../ActionGoals/constants'
import { ActionGoalSummariesList } from './ActionGoalSummariesList'
import { planCalendarDateSelector } from './models/plan.selectors'

const actionGoalNavigationBarProps = {
  dateSelectorType: 'plan' as const,
  allowRangeSelection: false,
  dateSelectorMode: DateSelectorMode.WEEK,
  allowFutureDates: false,
  hideCalendarIcon: true,
}

const ARC_SIZE = 200

export const PlanScreen = () => {
  const navigation = useNavigation()
  const dispatchAsync = useDispatchAsync()
  const styles = useStyleSheet(themedStyle)
  const isDataLoading = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showSpinner, setShowSpinner] = useState(false)

  const planCalendar = useSelector(planCalendarDateSelector)
  const actionGoalSummaries = useSelector(summariesForWeekSelector(planCalendar.startDate))
  const summariesCached = useSelector(isSummaryForWeekCachedSelector(planCalendar.startDate))

  const shouldLockToPortrait = useShouldLockToPortrait()
  const viewingCurrentWeek = planCalendar.startDate === startOfCurrentWeek()
  const actionGoalsEmpty = actionGoalSummaries.length === 0

  useRefreshNotificationsOnFocus()

  useLazyScreenPreload('ActionGoalListScreen')
  useLazyScreenPreload('ActionGoalFormModal')

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (shouldLockToPortrait) {
        OrientationDirector.lockTo(Orientation.portrait)
      }
    })
    return unsubscribe
  }, [navigation, shouldLockToPortrait])

  const refreshData = useCallback(
    async ({ isManualRefresh }: { isManualRefresh: boolean }) => {
      if (isDataLoading.current) {
        return
      }

      const promises = [
        dispatchAsync({
          type: 'users/fetch',
        }),
        dispatchAsync({
          type: 'nutritionistHub/fetchCoachAssignments',
        }),
        dispatchAsync({
          type: 'actionGoals/fetchSummaries',
          payload: {
            startDate: planCalendar.startDate,
          },
        }),
      ]

      isDataLoading.current = true
      setIsLoading(true)
      if (isManualRefresh) {
        setShowSpinner(true)
      }

      try {
        await Promise.all(promises)
      } catch {
        // do nothing
      } finally {
        isDataLoading.current = false
        setIsLoading(false)
        if (isManualRefresh) {
          setShowSpinner(false)
        }
      }
    },
    [dispatchAsync, planCalendar.startDate],
  )

  const loadDataOnFocus = useCallback(() => {
    refreshData({ isManualRefresh: false })
  }, [refreshData])

  useFocusEffect(loadDataOnFocus)

  const refreshControl = (
    <CommonRefreshControl
      refreshing={isLoading && showSpinner}
      onRefresh={() => refreshData({ isManualRefresh: true })}
    />
  )

  const hasLargeScreen = useHasLargeScreen()

  const banners = (
    <>
      <OnboardingChecklistBanner />
      <VideoCallsUpsellBanner />
    </>
  )

  const actionGoalsOverviewSection = (
    <View style={styles.overviewSection}>
      {actionGoalsEmpty && viewingCurrentWeek ? (
        <>
          <View style={styles.actionGoalEmptyStateIconContainer}>
            <StreakIcon width={48} height={48} />
          </View>
          <View style={styles.actionGoalEmptyStateHeader}>
            <Text type="title-3" style={styles.actionGoalEmptyStateText}>
              Let's build your personalized plan
            </Text>
            <Text type="large" style={styles.actionGoalEmptyStateText}>
              Track the goals that most matter to you, like glucose, nutrition, fitness and more.
            </Text>
          </View>
        </>
      ) : (
        <ActionGoalProgress summaries={actionGoalSummaries} hideDescription={!viewingCurrentWeek} />
      )}
    </View>
  )

  const actionGoalsSection = (
    <View style={styles.weeklyGoalsSection}>
      {viewingCurrentWeek && (
        <View style={styles.weeklyGoalsHeader}>
          <View style={styles.weeklyGoalsHeaderText}>
            <Text bold type="large">
              Your Goals
            </Text>
            <Text type="regular" style={styles.weeklyGoalsHeaderSubtitle}>
              Track your weekly goals and habits.
            </Text>
          </View>
          {!actionGoalsEmpty && (
            <Button
              type="outline"
              size="small"
              accessibilityLabel="Add Weekly Goal"
              onPress={() => navigation.navigate('ActionGoalListScreen')}
              accessoryLeft={<Icon name="plus" size={16} weight="bold" />}
            >
              Add
            </Button>
          )}
        </View>
      )}
      {!viewingCurrentWeek && actionGoalsEmpty && (
        <View style={styles.actionGoalEmptyHistoryHeader}>
          <Text type="title-3">No goals recorded for this week</Text>
        </View>
      )}
      <ActionGoalSummariesList
        summaries={actionGoalSummaries}
        allowEditing={viewingCurrentWeek}
        showEmptyCards={viewingCurrentWeek}
        dateRange={{ startDate: planCalendar.startDate, endDate: planCalendar.endDate }}
      />
    </View>
  )

  const loadingContent = (
    <ContentLoader style={styles.loader}>
      <Rect
        x="50%"
        width={ARC_SIZE}
        height={ARC_SIZE * 0.9}
        translateX={-ARC_SIZE / 2}
        rx="12"
        ry="12"
      />

      <View style={{ marginTop: ARC_SIZE, paddingHorizontal: 16 }}>
        {Array.from({ length: Math.max(actionGoalSummaries.length, MIN_ACTION_GOAL_CARDS) }).map(
          (_, index) => (
            <View
              key={index}
              style={[styles.actionGoalSummariesLoadingContainer, { height: SUMMARY_CARD_HEIGHT }]}
            >
              <ActionGoalSummaryLoadingCard />
            </View>
          ),
        )}
      </View>
    </ContentLoader>
  )

  return (
    <AccountAvatarNavigationBar
      style={styles.container}
      hideLeftIcon={hasLargeScreen}
      navigationBarProps={actionGoalNavigationBarProps}
    >
      {isLoading && !summariesCached ? (
        loadingContent
      ) : (
        <>
          {banners}
          <ScrollView refreshControl={refreshControl}>
            <LiveSurfaceBanner screen={NotificationEngineScreen.Plan} />
            {actionGoalsOverviewSection}
            {actionGoalsSection}
          </ScrollView>
        </>
      )}
    </AccountAvatarNavigationBar>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  loader: {
    marginTop: 16,
    flex: 1,
  },
  overviewSection: {
    marginVertical: 8,
    alignItems: 'center',
  },
  actionGoalEmptyStateHeader: {
    marginHorizontal: 32,
    alignItems: 'center',
    gap: 8,
  },
  actionGoalEmptyStateIconContainer: {
    marginBottom: 16,
  },
  actionGoalEmptyStateText: {
    textAlign: 'center',
  },
  focusItemsOnboardingCard: {
    marginTop: 8,
  },
  weeklyGoalsSection: {
    flex: 1,
    marginTop: 16,
    paddingHorizontal: 16,
    marginBottom: 32,
  },
  weeklyGoalsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  weeklyGoalsHeaderText: {
    flex: 1,
    flexDirection: 'column',
  },
  weeklyGoalsHeaderSubtitle: {
    color: 'theme.text.secondary',
  },
  actionGoalEmptyHistoryHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  actionGoalSummariesLoadingContainer: {
    marginBottom: 12,
  },
})
